import { useMemo, useEffect, useState, useRef } from "react";

// Helpers
import { ComplexCommission, ComplexCommissionTier } from "@/PayPlans/helpers/DefaultSections";

// Hooks
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import DoubleCheckingButton from "@/Layouts/components/Buttons/DoubleCheckingButton";

// Tiers Layouts
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";
import TierColumns from "@/Layouts/components/Tiers/TierColumns";
import TierColumnsItem from "@/Layouts/components/Tiers/TierColumnsItem";
import TierRows from "@/Layouts/components/Tiers/TierRows";

// Custom components
import MatrixColumn from "./MatrixColumn";
import MatrixRow from "./MatrixRow";
import OpCode from "./OpCode";

// MUI
import { Grid, Stack, Collapse, Tooltip, SvgIcon, Typography, FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { TransitionGroup } from "react-transition-group";

// Icons
import InfoIcon from "@Washouts/assets/icons/info-grey.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/actions/trash.svg";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SimpleIconButton from "@Layouts/components/Buttons/SimpleIconButton";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";

// Types
import {
	TTierCommission,
	ITiers,
	TargetedPerformanceBonusContent,
	IOnActionUpdateArgs,
	IOnChangeTypeArgs,
	ISelection,
	IOpCode,
	IOpCodeExt,
	UpdateValueRowArgs,
	UpdateValueColumnArgs,
	OnActionUpdateTabParams,
	ModelKeys,
} from "../../../types/sections/technicians";

const minimumColumns = 1;
const maximumColumns = 4;
const maximumTiers = Number.MAX_VALUE;

const minimumHoursInput = 0;
const maximumHoursInput = 744;
const maximumNumber = Number.MAX_VALUE;

const isDuration = (input: string) => input.toLowerCase() === "duration";

const isDollar = (input: string) => input.toLowerCase() === "dollar";

const isDollarPerDuration = (input: string) => input.toLowerCase() === "dollar per duration";

const getMaxAmount = (input: string) => {
	if (isDuration(input)) {
		return maximumHoursInput;
	} else if (isDollar(input) || isDollarPerDuration(input)) {
		return maximumNumber;
	}
	return 100;
};

const updateValueColumn = (args: UpdateValueColumnArgs) => {
	const { currentTiers, column, updatedValue, area } = args;

	currentTiers.forEach((tier: ITiers) => {
		tier.commissions?.forEach((commission, key) => {
			if (area in commission && key === column) {
				commission[area]["value"] = updatedValue;
				if (tier.commissions?.[key + 1]) {
					tier.commissions[key + 1]["from"]["value"] = updatedValue;
				}
			}
		});
	});
};

const updateValueRow = (args: UpdateValueRowArgs) => {
	const { currentTiers, row, column, updatedValue, area } = args;

	if (area === "to" || area === "from") {
		currentTiers[row][area]["value"] = updatedValue;
		if (currentTiers?.[row + 1] && area === "to") {
			const diff = isDuration(currentTiers[row + 1]["to"]["input"]) ? 0 : 0.01;
			currentTiers[row + 1]["from"]["value"] = updatedValue + diff;
		}
	} else {
		currentTiers[row]["commissions"][column][area]["value"] = updatedValue;
	}
};

const timeLapse: ISelection[] = [
	{ label: "RO Creation Date", value: "RO Creation Date" },
	{ label: "In Stock Date", value: "In Stock Date" },
	{ label: "Purchase Date", value: "Purchase Date" },
];

const defaultDisabledOptions = ["In Stock Date", "Purchase Date"];

const payTypes: ISelection[] = [
	{ label: "Customer", value: "Customer" },
	{ label: "Internal", value: "Internal" },
	{ label: "Warranty", value: "Warranty" },
];

const defaultPayType = [{ label: "Customer", value: "Customer" }];

const isValidOperationCodes = (codes: IOpCodeExt[]) => {
	const codesArray: string[] = codes.map((code) => code.code);
	return (
		codes.length &&
		codes.every((code: IOpCodeExt) => code.code && code.type && code.isValid) &&
		!codesArray.filter((code: string, index: number, self: string[]) => self.indexOf(code) !== index).length
	);
};

const getSideTitle = (currentTiers: ITiers[], component: string) => {
	const SIDE_TITLE: Record<string, string> = {
		DURATION: "Tot. Pay Type Hours",
		LABOR: "Total Labor Gross",
		PARTS: "Total Parts Gross",
	};
	return isDuration(currentTiers[0]?.to.input) && component === "LABOR" ? SIDE_TITLE.DURATION : SIDE_TITLE[component];
};

const getSideSx = (currentTiers: ITiers[], component: string) => {
	const SIDE_TITLE: Record<string, Record<string, number>> = {
		DURATION: { top: 112, left: -28 },
		LABOR: { top: 112, left: -16 },
		PARTS: { top: 112, left: -14 },
	};
	if (component === "LABOR") {
		return isDuration(currentTiers[0]?.to.input) ? SIDE_TITLE.DURATION : SIDE_TITLE.LABOR;
	} else {
		return SIDE_TITLE.PARTS;
	}
};

type OperationCodesProps = {
	sectionId: string;
	tabKey: ModelKeys;
	content?: TargetedPerformanceBonusContent;
	deleteTitle?: string;
	readOnly: boolean;
	index?: number;
	onActionUpdate: (params: OnActionUpdateTabParams) => void;
	onActionDelete?: () => void;
	disabledSelector?: boolean;
	externalOpCodes?: IOpCode[];
	container?: boolean;
};

export default function OperationCodes(props: OperationCodesProps) {
	const {
		tabKey = "operationCodes",
		index = 0,
		content = {
			component: "LABOR",
			tiers: [],
			applyTiersRetroactively: false,
			selectedTimeLapse: "",
			selectedPayType: ["Customer"],
			selectedOperationCodes: [
				{
					code: "",
					type: "Generic",
					pay: [],
				},
			],
		},
		deleteTitle = "Remove Operation Codes and Pay Type",
		readOnly = false,
		onActionUpdate,
		onActionDelete = false,
		disabledSelector = false,
		externalOpCodes = [],
		container = true,
	} = props;

	// Hooks
	const { errors, isValid, getSectionsErrorMessages, validate } = useValidations();

	const localErrorsMessages = getSectionsErrorMessages(["tierColumns", "tierRows", "commissions"]);
	const headerErrors = errors?.get("tierColumns");
	const rowsErrors = errors?.get("tierRows");
	const columnsErrors = errors?.get("commissions");

	// States
	const [innerTiers, setInnerTiers] = useState<ITiers[]>([]);
	const [applyTiersRetroactively, setApplyTiersRetroactively] = useState(false);
	const [selectedPayType, setSelectedPayType] = useState<ISelection[]>(defaultPayType);
	const [selectedTimeLapse, setSelectedTimeLapse] = useState<ISelection>();
	const [selectedOperationCodes, setSelectedOperationCodes] = useState<IOpCodeExt[]>([]);
	const [component, setComponent] = useState("LABOR");
	const [disabledOptions, setDisabledOptions] = useState<string[]>(defaultDisabledOptions);
	const [allOpCodes, setAllOpCodes] = useState<IOpCode[]>([]);
	const [tierCondition, setTierCondition] = useState(false);
	const [sideTitle, setSideTitle] = useState<string>("Tot. Pay Type Hours");
	const [sideSx, setSideSx] = useState<Record<string, number>>({ top: 112, left: -28 });

	// Control to send update actions
	const wasUpdated = useRef(false);

	const checkDollarAndUnitPerDollar = (tiers: ITiers[]) => {
		if (!tiers.length) {
			return;
		}
		if (isDollar(tiers[0].to.input) && isDollarPerDuration(tiers[0].commissions[0].commission.input)) {
			setTierCondition(true);
			setApplyTiersRetroactively(true);
		} else {
			setTierCondition(false);
		}
	};

	useEffect(() => {
		if (wasUpdated.current) {
			const validToSave = Boolean(
				isValid && selectedPayType?.length && selectedTimeLapse && isValidOperationCodes(selectedOperationCodes)
			);
			const modelToPropagate = {
				tiers: [...innerTiers],
				applyTiersRetroactively: applyTiersRetroactively,
				selectedPayType: selectedPayType?.map((sel: ISelection) => sel.value) || [],
				selectedTimeLapse: selectedTimeLapse?.value ?? "",
				component: component,
				selectedOperationCodes:
					selectedOperationCodes?.map((sel: IOpCodeExt) => ({ code: sel.code, type: sel.type, pay: sel.pay })) || [],
			};
			onActionUpdate?.({ tabKey, index, modelToPropagate, isValid: validToSave });
			wasUpdated.current = false;
		}
	}, [isValid, innerTiers, applyTiersRetroactively, selectedPayType, selectedTimeLapse, component, selectedOperationCodes]);

	// Methods
	const handleAddColumn = () => {
		const currentTiers: ITiers[] = [...innerTiers];
		const commissionInput = currentTiers?.[0]?.["commissions"]?.[0]?.["commission"]?.["input"] || "Duration";
		const lastIndex = currentTiers?.[0]?.["commissions"]?.length ? currentTiers[0]["commissions"].length - 1 : 0;

		currentTiers.forEach((tier: ITiers) => {
			tier.commissions.push(
				ComplexCommission([minimumHoursInput, "Unit"], [minimumHoursInput, "Unit"], {
					identifier: "commission",
					commission: [0, commissionInput],
				})
			);
			if (tier?.commissions?.[lastIndex]) {
				tier.commissions[lastIndex]["to"]["value"] = minimumHoursInput;
			}
		});

		update(currentTiers);
	};

	const handleDeleteColumn = () => {
		const currentTiers: ITiers[] = [...innerTiers];
		if (currentTiers?.[0]?.["commissions"]?.length > minimumColumns) {
			currentTiers.forEach((tier: ITiers) => {
				tier.commissions.splice(-1);
				const lastIndex = tier?.commissions?.length ? tier.commissions.length - 1 : 0;
				tier.commissions[lastIndex]["to"]["value"] = minimumHoursInput;
			});
		}

		update(currentTiers);
	};

	const handleAddTier = () => {
		const currentTiers: ITiers[] = [...innerTiers];
		const lastIndex = currentTiers?.length ? currentTiers.length - 1 : 1;
		const lastInput = currentTiers?.[lastIndex]?.["from"]["input"] || "Duration";
		const commissionInput = currentTiers?.[lastIndex]?.["commissions"]?.[0]?.["commission"]?.["input"] || "Duration";
		const previousCommissions = currentTiers?.[lastIndex]?.["commissions"] || [];

		const commissions = previousCommissions.map((commission: TTierCommission) => {
			const previousFromInput = commission?.["from"]?.["input"] || "Unit";
			const previousFrom = commission?.["from"]?.["value"] || 0;
			const previousTo = commission?.["to"]?.["value"] || 0;
			const previousToInput = commission?.["to"]?.["input"] || "Unit";

			return ComplexCommission([previousFrom, previousFromInput], [previousTo, previousToInput], {
				identifier: "commission",
				commission: [0, commissionInput],
			});
		});

		// If the type is not duration, then it is a percent, use 100 as max
		const maxInput = getMaxAmount(lastInput);
		currentTiers.push(
			ComplexCommissionTier([minimumHoursInput, lastInput], [maxInput, lastInput], {
				identifier: "commissions",
				commissions,
			}) as unknown as ITiers
		);

		if (currentTiers?.length) {
			currentTiers[lastIndex].to.value = minimumHoursInput;
		}

		update(currentTiers);
	};

	const handleDeleteTier = () => {
		const currentTiers = [...innerTiers];
		if (currentTiers.length >= 1) {
			currentTiers.splice(-1);
		}
		const lastIndex = currentTiers?.length ? currentTiers.length - 1 : 1;
		const maxValue = getMaxAmount(currentTiers[lastIndex].to.input);
		currentTiers[lastIndex].to.value = maxValue;

		update(currentTiers);
	};

	const handleUpdateValue = (params: IOnActionUpdateArgs) => {
		const { row, column, location, area, value } = params;
		const currentTiers = [...innerTiers];
		const updatedValue = value ? Number(value) : 0;

		switch (location) {
			case "column":
				updateValueColumn({ currentTiers, column, updatedValue, area });
				break;
			case "row":
				updateValueRow({ currentTiers, row, column, updatedValue, area });
				break;
			default:
				break;
		}

		update(currentTiers);
	};

	const handleUpdateInput = (params: IOnChangeTypeArgs) => {
		const { location, area, input } = params;
		const currentTiers = [...innerTiers];
		switch (location) {
			case "column":
				break;
			case "row":
				currentTiers.forEach((tier) => {
					tier.commissions?.forEach((commission: TTierCommission) => {
						if (area in commission) {
							commission[area]["input"] = input;
							commission[area]["value"] = 0;
						}
					});
				});
				break;
			default:
				break;
		}
		checkDollarAndUnitPerDollar(currentTiers);
		update(currentTiers);
	};

	const setInputForHeader = (location: string, input: string, newComponentValue?: string) => {
		const currentTiers = [...innerTiers];
		const maxValue = getMaxAmount(input);
		switch (location) {
			case "column":
				break;
			case "row":
				currentTiers.forEach((tier, index) => {
					tier.from["input"] = input;
					tier.from["value"] = 0;
					tier.to["input"] = input;
					tier.to["value"] = index === currentTiers.length - 1 ? maxValue : 0;
				});
				break;
			default:
				break;
		}
		checkDollarAndUnitPerDollar(currentTiers);
		setSideTitle(getSideTitle(currentTiers, newComponentValue ?? component));
		setSideSx({ ...getSideSx(currentTiers, newComponentValue ?? component) });
		update(currentTiers);
	};

	const handleUpdateInputForHeader = (params: IOnChangeTypeArgs) => {
		const { location, input } = params;
		setInputForHeader(location, input);
	};

	const update = (updatedTiers: ITiers[] = []) => {
		wasUpdated.current = true;
		setInnerTiers([...updatedTiers]);
	};

	const handleUpdateSwitch = (value: boolean) => {
		wasUpdated.current = true;
		setApplyTiersRetroactively(value);
	};

	const onChangePayType = (value: any[]) => {
		wasUpdated.current = true;
		setSelectedPayType([...(value as ISelection[])]);
		setDisabledOptions(value.find((val: ISelection) => val.value === "Internal") ? [] : defaultDisabledOptions);
		if (value.some((val: ISelection) => val.value === "Internal")) {
			setDisabledOptions([]);
		} else {
			setDisabledOptions(defaultDisabledOptions);
			if (selectedTimeLapse?.value !== "RO Creation Date") {
				setSelectedTimeLapse(undefined);
			}
		}
		const updated = selectedOperationCodes.map((op) => ({
			code: op.code,
			type: op.type,
			pay: value.map((selPT) => selPT.value),
			isValid: op.isValid,
		}));
		setSelectedOperationCodes([...updated]);
	};

	const onChangeTimeLapse = (value: any) => {
		wasUpdated.current = true;
		setSelectedTimeLapse(value as ISelection);
	};

	const onChangeLaborParts = (value: string) => {
		wasUpdated.current = true;
		setComponent(value);
		if (value === "PARTS") {
			setInputForHeader("row", "Percent", value);
		} else {
			setSideTitle(getSideTitle(innerTiers, value));
			setSideSx({ ...getSideSx(innerTiers, value) });
		}
	};

	const onOpCodeChange = ({
		code,
		type,
		pay,
		isValid,
		index,
	}: {
		code: string;
		type: string;
		pay: string[];
		isValid: boolean;
		index: number;
	}) => {
		wasUpdated.current = true;
		const updated = [...selectedOperationCodes];
		updated[index] = {
			code: code,
			type: type,
			pay: pay,
			isValid: isValid,
		};
		setSelectedOperationCodes([
			...updated.map((up) => ({
				...up,
				type: type,
			})),
		]);
	};

	useEffect(() => {
		setAllOpCodes([...selectedOperationCodes.map((up) => ({ code: up.code, type: up.type, pay: up.pay }))]);
	}, [selectedOperationCodes]);

	const addOpCodes = () => {
		const updated = [...selectedOperationCodes];
		updated.push({
			code: "",
			type: updated.length ? updated[0].type : "",
			pay: selectedPayType.map((selPT) => selPT.value),
			isValid: false,
		});
		setSelectedOperationCodes([...updated]);
	};

	const onOpCodeDel = (index: number) => {
		wasUpdated.current = true;
		const updated = [...selectedOperationCodes];
		updated.splice(index, 1);
		setSelectedOperationCodes([...updated]);
	};

	useMemo(() => {
		if (innerTiers?.length) {
			validate([
				{
					definition: {
						useKey: "tierColumns",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: ">",
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid column tiers - must be in descending order",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers?.[0]?.commissions || [],
				},
				{
					definition: {
						useKey: "tierRows",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<",
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid row tiers - must be in ascending order",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers || [],
				},
				{
					definition: {
						useKey: "commissions",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: "<=", // If current <= next is good,
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<=", // If upper <= lower is good,
									deltas: { Duration: 0, Dollar: 0, Percent: 0 },
								},
								{
									order: "DIAGONAL",
									on: "value",
									compare: "<",
								},
							],
						},
						errorsMap: ["commission"],
						errorsMapOn: "commissions",
						message: "Incorrect commissions definition",
						validationType: "MATRIX",
					},
					section: innerTiers || [],
				},
			]);
		}
	}, [innerTiers, validate]);

	useEffect(() => {
		if (content) {
			wasUpdated.current = false;
			const {
				tiers,
				applyTiersRetroactively: applyRetroactively,
				selectedTimeLapse,
				selectedPayType,
				selectedOperationCodes,
				component,
			} = content;
			if (component) {
				setComponent(component);
			}
			setInnerTiers([...tiers]);
			setApplyTiersRetroactively(applyRetroactively);
			const pays = selectedPayType?.map((selPay: string) => {
				const { label, value } = payTypes.find((pay: ISelection) => pay.value === selPay) ?? {
					label: selPay,
					value: selPay,
				};
				return { label, value };
			}) as ISelection[];
			setSelectedPayType(pays || []);
			setDisabledOptions(pays?.find((pay: ISelection) => pay.value === "Internal") ? [] : defaultDisabledOptions);
			setSelectedTimeLapse(
				selectedTimeLapse
					? {
							label: selectedTimeLapse,
							value: selectedTimeLapse,
						}
					: undefined
			);
			const codesUpdated = selectedOperationCodes?.map((code) => ({
				code: code.code,
				type: code.type,
				pay: code.pay,
				isValid: true,
			}));
			setSelectedOperationCodes([...(codesUpdated ?? [])]);
			checkDollarAndUnitPerDollar(tiers);
			setSideTitle(getSideTitle(tiers, component));
			setSideSx({ ...getSideSx(tiers, component) });
		}
	}, [content]);

	return (
		<Grid container={container} item mt={1} mb={3} xs={12}>
			<Stack direction={"row"} justifyContent={"flex-end"} flex={1} mr={5} pb={1}>
				<CustomizedSwitches
					key={"applyTiersRetroactively"}
					textChecked={"Apply retroactively"}
					textUnChecked={"Apply retroactively"}
					checked={applyTiersRetroactively}
					disabled={readOnly || tierCondition}
					onChange={handleUpdateSwitch}
				/>
			</Stack>

			<Grid item xs={12}>
				<Stack direction={"row"} ml={3.5} mb={1}>
					<FormControl>
						<RadioGroup
							row
							aria-labelledby="range-buttons-group"
							value={component}
							name="range-buttons-group"
							className={"radio-buttons-small regular-blue-color"}
							onChange={(e) => onChangeLaborParts(e.target.value)}
							sx={{
								"& .MuiFormControlLabel-root": {
									marginRight: "24px",
								},
							}}
						>
							<FormControlLabel
								disabled={readOnly || disabledSelector}
								value="LABOR"
								control={<Radio />}
								label="Labor"
								sx={{
									".MuiFormControlLabel-label": {
										fontFamily: "var(--font-family-v1)",
										color: "var(--text-color)",
										fontSize: "14.5px",
										whiteSpace: "nowrap",
									},
								}}
							/>
							<FormControlLabel
								disabled={readOnly || disabledSelector}
								value="PARTS"
								control={<Radio />}
								label="Parts"
								sx={{
									"& .MuiTypography-root": {
										fontFamily: "var(--font-family-v1)",
										color: "var(--text-color)",
										fontSize: "14.5px",
										whiteSpace: "nowrap",
									},
								}}
							/>
						</RadioGroup>
					</FormControl>
				</Stack>

				<Stack direction={"row"} ml={4} mb={4} alignItems="center" flexWrap="wrap">
					<Stack mr={3}>
						<Typography className="!text-[14.5px] !font-sans" sx={{ color: "var(--text-color)" }}>
							Pay Type
						</Typography>
						<SmartSelector
							popperProps={{ className: "!z-[1350]" } as object}
							multiple={true}
							selectBy={"value"}
							showBy={"label"}
							options={payTypes}
							disabled={readOnly}
							selected={selectedPayType}
							error={!Boolean(selectedPayType.length)}
							onChangeAction={(value) => onChangePayType(value as any[])}
							sx={{ width: "110px", mt: "5px" }}
							inputProps={{
								placeholder: "Pay Type Hours",
								hideTooltip: true,
							}}
							tagsVariant="text"
						/>
					</Stack>

					{selectedOperationCodes.map((opCode: IOpCode, index: number) => (
						<OpCode
							key={index}
							selCode={opCode.code}
							selType={opCode.type}
							payType={opCode.pay}
							readOnly={readOnly}
							index={index}
							onActionUpdate={onOpCodeChange}
							onActionDelete={onOpCodeDel}
							removeDisabled={selectedOperationCodes.length <= 1}
							allOpCodes={[...allOpCodes]}
							externalOpCodes={[...externalOpCodes]}
						/>
					))}

					<Stack sx={{ mt: "24px" }}>
						<SimpleIconButton
							className="btn-simple-add !text-primary"
							ariaLabel="Add"
							icon={<AddCircleOutlineRoundedIcon fontSize="medium" />}
							iconHover={<AddCircleRounded fontSize="medium" />}
							sx={{ padding: "3px" }}
							loadingSize={20}
							sxLoading={{ padding: "2px" }}
							onClick={addOpCodes}
						/>
					</Stack>

					{onActionDelete && (
						<Stack sx={{ mt: "24px" }}>
							<DoubleCheckingButton
								title={deleteTitle}
								icon={<CloseRoundedIcon sx={{ fontSize: "28px" }} />}
								iconHover={<CloseRoundedIcon sx={{ fontSize: "28px" }} />}
								endIcon={<SvgIcon className="!text-[16px] ml-[4px]" component={TrashIcon} viewBox={"0 0 14 14"} />}
								confirmationButtonClasses={
									"!min-w-[260px] hover:!border-gray-blue hover:!bg-gray-blue hover:!shadow-gray-blue"
								}
								mainButtonClasses={"btn-simple-remove"}
								mainSx={{ marginLeft: "10px" }}
								confirmationSx={{ right: 0, left: "10px" }}
								disabled={readOnly}
								onDeleteAction={() => onActionDelete?.()}
							/>
						</Stack>
					)}
				</Stack>

				<TierColumns
					useComponent={true}
					component={
						<>
							<Stack mr={2} direction={"row"}>
								<Typography className="tiered-title">Time Lapse</Typography>
								<Tooltip
									title={"Descending Amounts"}
									placement={"top"}
									arrow
									sx={{ fontFamily: "PT Sans Narrow" }}
									style={{ float: "right", marginLeft: "5px" }}
								>
									<img
										src={InfoIcon}
										alt="info"
										height="10px"
										width="10px"
										style={{ alignSelf: "start", marginLeft: "5px" }}
									/>
								</Tooltip>
							</Stack>
							<Stack direction={"row"} mt={-1}>
								<SmartSelector
									popperProps={{ className: "!z-[1350]" } as object}
									selectBy={"value"}
									showBy={"label"}
									options={timeLapse}
									disabled={readOnly}
									disabledOptions={disabledOptions}
									selected={selectedTimeLapse}
									error={!Boolean(selectedTimeLapse)}
									onChangeAction={(value) => onChangeTimeLapse(value as any[])}
									sx={{ width: "160px", marginTop: "20px" }}
									inputProps={{
										placeholder: "Internal Time Lapse Reference",
										hideTooltip: true,
										sx: { width: "180px" },
									}}
									tagsVariant="text"
								/>
								<Tooltip
									title={
										<Typography sx={{ width: "120px" }} className="!font-sans !text-[12px]">
											Cust. and Warr. pay lapse ref. are set to RO Creation Date
										</Typography>
									}
									placement={"top"}
									arrow
									style={{}}
								>
									<img
										src={InfoIcon}
										alt="info"
										height="10px"
										width="10px"
										style={{ alignSelf: "start", marginLeft: "0px", position: "relative", right: "-25px", top: "20px" }}
									/>
								</Tooltip>
							</Stack>
						</>
					}
					titleContainerProps={{
						justifyContent: "flex-start",
						alignItems: "end",
						sx: { maxWidth: "248px", width: "248px", marginRight: "3px" },
					}}
					disabledDelete={innerTiers?.[0]?.commissions?.length <= minimumColumns || readOnly}
					disabledAdd={innerTiers?.[0]?.commissions?.length >= maximumColumns || readOnly}
					onClickDelete={handleDeleteColumn}
					onClickAdd={handleAddColumn}
				>
					<Stack direction={"row"} position={"relative"}>
						{innerTiers?.[0]?.commissions?.map((tier: TTierCommission, key: number) => {
							const indexKey = `targeted_performance_bonus_departments_column_${key}`;
							const fromDisabled = Boolean(!key);
							const toDisabled = Boolean(innerTiers?.[0]?.commissions.length === key + 1);

							return (
								<TierColumnsItem key={indexKey} index={key} title={"Bonus"}>
									<MatrixColumn
										index={key}
										item={tier}
										errors={headerErrors?.length ? headerErrors : []}
										fromDisabled={fromDisabled}
										toDisabled={toDisabled}
										readOnly={readOnly}
										onActionUpdate={handleUpdateValue}
									/>
								</TierColumnsItem>
							);
						})}
					</Stack>
				</TierColumns>
			</Grid>
			<Grid item xs={12} mt={1} ml={2} minHeight={"140px"}>
				<TierRows
					showTitle
					title={sideTitle}
					sxTitlePropsContainer={sideSx}
					titleProps={{
						className: "tiered-title",
						sx: { transform: "rotate(270deg)" },
						icon: {
							present: true,
							text: "Ascending Amounts",
						},
					}}
					centerTitleAfterRow={2}
					deleteButtonText={"Remove Tier"}
					addButtonText={"Add Tier"}
					deleteButtonDisabled={innerTiers?.length <= 1 || readOnly}
					addButtonDisabled={innerTiers?.length >= maximumTiers || readOnly}
					spaces={{
						mainSection: 235,
						columns: 170,
						totalColumns: innerTiers?.[0]?.commissions?.length || 1,
						totalRows: innerTiers?.length || 1,
						separation: 5.5,
					}}
					rowsAreaProps={{
						ml: "35px",
					}}
					onClickDelete={handleDeleteTier}
					onClickAdd={handleAddTier}
				>
					<TransitionGroup>
						<Collapse>
							<Stack direction="column" rowGap={2}>
								{innerTiers?.map((tier: ITiers, key: number) => {
									const indexKey = `targeted_performance_bonus_departments_row_${key}`;
									const fromDisabled = Boolean(!key);
									const toDisabled = Boolean(innerTiers?.length === key + 1);
									const commissionsLength = innerTiers?.[0]?.commissions?.length || 0;
									const reduction = commissionsLength * 5 + 70 + commissionsLength;
									const firstInputData: any = [];
									const secondInputData: any = [];
									if (component === "LABOR") {
										firstInputData.push({
											type: "Duration",
											min: 0,
											max: 744,
											decimals: 2,
											decimalsEditable: true,
											dashed: fromDisabled,
											exact: true,
										});
										secondInputData.push({
											type: "Duration",
											min: 0,
											max: 744,
											decimals: 2,
											decimalsEditable: true,
											dashed: toDisabled,
											exact: true,
										});
									}
									firstInputData.push(
										{
											type: "Percent",
											min: 0,
											max: 100,
											decimalsEditable: true,
											decimals: 2,
											dashed: fromDisabled,
										},
										{
											type: "Dollar",
											min: 0,
											max: 100000000,
											decimals: 2,
											decimalsEditable: true,
											dashed: fromDisabled,
										}
									);
									secondInputData.push(
										{
											type: "Percent",
											min: 0,
											max: 100,
											decimalsEditable: true,
											decimals: 2,
											dashed: toDisabled,
										},
										{
											type: "Dollar",
											min: 0,
											max: 100000000,
											decimals: 2,
											decimalsEditable: true,
											dashed: toDisabled,
										}
									);

									return (
										<Grid key={indexKey} container item ml={2}>
											<MatrixRow
												index={key}
												item={tier}
												fromDisabled={false}
												toDisabled={false}
												spaces={{
													mainSection: 235,
													columns: 170,
													totalColumns: innerTiers?.[0]?.commissions?.length || 1,
													totalRows: innerTiers?.length || 1,
													separation: 8.5,
													reduction,
												}}
												rowsErrors={rowsErrors?.length ? rowsErrors : []}
												columnsErrors={columnsErrors?.length ? columnsErrors : []}
												readOnly={readOnly}
												spacing={"9px"}
												sxMainContent={{ minWidth: "170.41px" }}
												sxCells={{ minWidth: "170px" }}
												firstInputDataProp={firstInputData}
												secondInputDataProp={secondInputData}
												onActionUpdate={handleUpdateValue}
												onChangeType={handleUpdateInput}
												onChangeTypeHeader={handleUpdateInputForHeader}
											/>
										</Grid>
									);
								})}
							</Stack>
						</Collapse>
					</TransitionGroup>

					{Boolean(localErrorsMessages?.length) && (
						<Grid container direction="column" rowSpacing={0.2} mt={1} ml={2} alignItems="flex-start">
							{localErrorsMessages.map((message, key) => {
								const indexKey = `error_targeted_performance_bonus_${key}`;
								return <TierAlertError key={indexKey} text={message} />;
							})}
						</Grid>
					)}
				</TierRows>
			</Grid>
		</Grid>
	);
}
